import { useMemo } from 'react';
import type { BracketTree, DrawPreviewMatch } from '@court-sync/shared';
import { buildBracketTree } from '@court-sync/shared';
import { cn } from '@/lib/utils';

interface PlayEventBracketTreeProps {
  matches: DrawPreviewMatch[];
  participantNamesById?: Record<string, string>;
  className?: string;
}

export function PlayEventBracketTree({
  matches,
  participantNamesById = {},
  className,
}: PlayEventBracketTreeProps) {
  const tree = useMemo(
    () => buildBracketTree(matches, participantNamesById),
    [matches, participantNamesById]
  );

  if (tree.rounds.length === 0) {
    return <p className="text-sm text-muted-foreground">No bracket to display.</p>;
  }

  return (
    <div className={cn('overflow-x-auto pb-2', className)}>
      <div className="flex min-w-max gap-4">
        {tree.rounds.map((round) => (
          <section key={round.roundNumber} className="flex w-56 shrink-0 flex-col gap-3">
            <h4 className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {round.label}
            </h4>
            <div className="flex flex-1 flex-col justify-around gap-3">
              {round.matches.map((match) => (
                <article
                  key={`${round.roundNumber}-${match.matchIndex}`}
                  className="rounded-lg border border-border bg-card p-3 shadow-sm"
                >
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    M{match.matchIndex + 1}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div
                      className={cn(
                        'rounded-md border px-2 py-1.5',
                        match.sideA.participantId
                          ? 'border-border bg-muted/30'
                          : 'border-dashed border-muted-foreground/30 text-muted-foreground'
                      )}
                    >
                      {match.sideA.label}
                    </div>
                    <div
                      className={cn(
                        'rounded-md border px-2 py-1.5',
                        match.sideB.participantId
                          ? 'border-border bg-muted/30'
                          : 'border-dashed border-muted-foreground/30 text-muted-foreground'
                      )}
                    >
                      {match.sideB.label}
                    </div>
                  </div>
                  {match.feedsIntoMatchIndex != null ? (
                    <p className="mt-2 text-[10px] text-muted-foreground">
                      Feeds into M{match.feedsIntoMatchIndex + 1}
                    </p>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Bracket updates as scores are recorded and winners advance.
      </p>
    </div>
  );
}

export function PlayEventBracketTreeFromTree({
  tree,
  className,
}: {
  tree: BracketTree;
  className?: string;
}) {
  if (tree.rounds.length === 0) {
    return <p className="text-sm text-muted-foreground">No bracket to display.</p>;
  }

  return (
    <div className={cn('overflow-x-auto pb-2', className)}>
      <div className="flex min-w-max gap-4">
        {tree.rounds.map((round) => (
          <section key={round.roundNumber} className="flex w-56 shrink-0 flex-col gap-3">
            <h4 className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {round.label}
            </h4>
            <div className="flex flex-1 flex-col justify-around gap-3">
              {round.matches.map((match) => (
                <article
                  key={`${round.roundNumber}-${match.matchIndex}`}
                  className="rounded-lg border border-border bg-card p-3 shadow-sm"
                >
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    M{match.matchIndex + 1}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="rounded-md border border-border bg-muted/30 px-2 py-1.5">
                      {match.sideA.label}
                    </div>
                    <div className="rounded-md border border-border bg-muted/30 px-2 py-1.5">
                      {match.sideB.label}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
